import { type JSONContent } from "novel";

// <p>狗娃子</p><p><strong>描述</strong>，</p><blockquote class=\"tt-blockquote\"><p>很好</p></blockquote>


// 获取 JSONContent 的Codex 标签列表
export const getCodexMarksFromJSONContent = (content: JSONContent): Array<{ id: string; text: string }> => {
  const annotations: Array<{ id: string; text: string }> = [];

  const traverse = (node: JSONContent) => {
    if (!node) return;

    // 检查当前节点是否有 codexAnnotation 标记
    if (node.marks) {
      const codexMark = node.marks.find(mark => mark.type === 'codexAnnotation');
      if (codexMark && codexMark.attrs?.codexAnnotationId && node.text) {
        annotations.push({
          id: codexMark.attrs.codexAnnotationId,
          text: node.text
        });
      }
    }

    // 递归遍历子节点
    if (node.content) {
      node.content.forEach(child => traverse(child));
    }
  };

  traverse(content);
  return annotations;
};
// 将 JSONContent 转换为 格式化文本
export const jsonContentToFormattedText = (content: JSONContent | JSONContent[], indentLevel: number = 0): string => {
  if (!content) return '';

  let result = '';
  const indent = ' '.repeat(indentLevel);

  if (Array.isArray(content)) {
    return content.map(child => jsonContentToFormattedText(child, indentLevel)).filter(text => text.trim()).join('\n');
  }

  // 处理不同类型的节点
  switch (content.type) {
    case 'doc':
      // 文档节点，递归处理子节点
      if (content.content) {
        result = content.content.map(child => jsonContentToFormattedText(child, indentLevel)).join('\n');
      }
      break;

    case 'paragraph':
      // 段落节点
      if (content.content) {
        const text = content.content.map(child => jsonContentToFormattedText(child, indentLevel)).join('');
        if (text.trim()) {
          result = indent + text;
        }
      }
      break;

    case 'text':
      // 文本节点
      result = content.text || '';
      break;

    case 'heading':
      // 标题节点
      if (content.content) {
        const level = content.attrs?.level || 1;
        const text = content.content.map(child => jsonContentToFormattedText(child, indentLevel)).join('');
        if (text.trim()) {
          result = indent + '#'.repeat(level) + ' ' + text;
        }
      }
      break;

    case 'bulletList':
      // 无序列表
      if (content.content) {
        result = content.content.map((item) => {
          const itemText = jsonContentToFormattedText(item, indentLevel + 2);
          return indent + '* ' + itemText;
        }).join('\n');
      }
      break;

    case 'orderedList':
      // 有序列表
      if (content.content) {
        result = content.content.map((item, index) => {
          const itemText = jsonContentToFormattedText(item, indentLevel + 2);
          return indent + (index + 1) + '. ' + itemText;
        }).join('\n');
      }
      break;

    case 'listItem':
      // 列表项
      if (content.content) {
        result = content.content.map(child => jsonContentToFormattedText(child, indentLevel)).join('\n');
      }
      break;

    case 'blockquote':
      // 引用块
      if (content.content) {
        const quoteContent = content.content.map(child => jsonContentToFormattedText(child, indentLevel)).join('\n');
        if (quoteContent.trim()) {
          // 为引用块的每一行添加 > 前缀
          result = quoteContent.split('\n').map(line => {
            if (line.trim()) {
              return indent + '> ' + line;
            }
            return line;
          }).join('\n');
        }
      }
      break;

    case 'aiGeneration':
      // 生成块：导出/转文本时只要内部正文
      if (content.content) {
        result = content.content
          .map((child) => jsonContentToFormattedText(child, indentLevel))
          .filter((t) => t.trim())
          .join('\n');
      }
      break;

    default:
      // 其他块级元素：直接展开子节点（勿加 >，那是 blockquote 的事）
      if (content.content) {
        result = content.content
          .map((child) => jsonContentToFormattedText(child, indentLevel))
          .filter((t) => t.trim())
          .join('\n');
      }
      break;
  }

  return result;
};

// 主函数：将 JSONContent 转换为指定格式的文本
export const convertJSONContentToFormattedText = (content: JSONContent | JSONContent[]): string => {
  if (!content) return '';

  const formattedText = jsonContentToFormattedText(content);

  // 清理空行并确保格式正确
  return formattedText
    // .split('\n')
    // .filter(line => line.trim() || line.includes('>')) // 保留有内容的行和引用块的空行
    // .join('\n')
    .trim();
};

// 将富文本（HTML）转换为格式化文本（类似 jsonContentToFormattedText 的输出格式）
export const htmlToFormattedText = (html: string, indentLevel: number = 0): string => {
  if (!html) return '';

  // 在浏览器环境中使用 DOMParser
  if (typeof DOMParser === 'undefined') {
    // 如果不是浏览器环境，尝试简单处理
    return html.replace(/<[^>]*>/g, '').trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const processNode = (node: Node, currentIndent: number = indentLevel): string => {
    if (!node) return '';

    // 文本节点
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      return text.trim() ? text : '';
    }

    // 元素节点
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();
      const indent = ' '.repeat(currentIndent);
      let result = '';

      // 处理子节点
      const childTexts: string[] = [];
      for (const child of Array.from(node.childNodes)) {
        const childText = processNode(child, currentIndent);
        if (childText) {
          childTexts.push(childText);
        }
      }
      const content = childTexts.join('');

      switch (tagName) {
        case 'p':
          // 段落节点
          if (content.trim()) {
            result = indent + content;
          }
          break;

        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          // 标题节点
          const level = parseInt(tagName.charAt(1)) || 1;
          if (content.trim()) {
            result = indent + '#'.repeat(level) + ' ' + content;
          }
          break;

        case 'blockquote':
          // 引用块
          if (content.trim()) {
            const quoteContent = content.split('\n').map(line => {
              if (line.trim()) {
                return indent + '> ' + line;
              }
              return line;
            }).join('\n');
            result = quoteContent;
          }
          break;

        case 'ul':
          // 无序列表
          if (content.trim()) {
            const items = Array.from(element.children).map((li) => {
              const itemText = processNode(li, currentIndent + 2);
              return indent + '* ' + itemText;
            });
            result = items.join('\n');
          }
          break;

        case 'ol':
          // 有序列表
          if (content.trim()) {
            const items = Array.from(element.children).map((li, index) => {
              const itemText = processNode(li, currentIndent + 2);
              return indent + (index + 1) + '. ' + itemText;
            });
            result = items.join('\n');
          }
          break;

        case 'li':
          // 列表项
          if (content.trim()) {
            result = content.split('\n').map(line => line.trim()).join('\n');
          }
          break;

        case 'strong':
        case 'b':
          // 粗体，保留标记
          result = '**' + content + '**';
          break;

        case 'em':
        case 'i':
          // 斜体，保留标记
          result = '*' + content + '*';
          break;

        case 'u':
          // 下划线，保留标签
          result = '<u>' + content + '</u>';
          break;



        case 'span':
        case 'div':
          // 容器元素，直接返回内容
          result = content;
          break;

        case 'br':
          // 换行
          result = '\n';
          break;

        case 's':
        case 'del':
        case 'strike':
          // 保留标签
          result = `<${tagName}>` + content + `</${tagName}>`;
          break;

        default:
          // 其他元素，递归处理子节点
          result = content;
          break;
      }

      return result;
    }

    return '';
  };

  // 处理body下的所有子节点
  const results: string[] = [];
  for (const child of Array.from(body.childNodes)) {
    const text = processNode(child, indentLevel);
    if (text.trim()) {
      results.push(text);
    }
  }

  return results.join('\n').trim();
};

// 主函数：将富文本（HTML）转换为指定格式的文本
export const convertHtmlToFormattedText = (html: string): string => {
  if (!html) return '';

  const formattedText = htmlToFormattedText(html);

  // 清理空行并确保格式正确
  return formattedText.trim();
};


